import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import {
    fetchUserTimelines,
    getUserProfile,
    createEvent,
    createTimeline,
    fetchMyReservations,
    createReservation as apiCreateReservation
} from 'api'; // Ensure this path is correct

// This function handles the grouping logic to avoid duplicate events in the UI.
const formatAndGroupEventData = (timelines = [], userProfile = null) => {
    // Note: userProfile is the full object from the API { user: {...} }
    if (!userProfile?.user || !timelines) return [];

    const groupedByEvent = timelines.reduce((acc, timeline) => {
        const { event } = timeline;
        if (!event?.id || !event?.startDate) return acc;
        const eventKey = `${event.id}-${new Date(event.startDate).toDateString()}`;

        if (!acc[eventKey]) {
            acc[eventKey] = {
                eventData: event,
                timelines: [],
                teams: [],
            };
        }

        acc[eventKey].timelines.push(timeline);
        if (timeline.team) {
            acc[eventKey].teams.push(timeline.team);
        }
        
        return acc;
    }, {});

    return Object.values(groupedByEvent).map(group => {
        const { eventData, timelines, teams } = group;
        let startDate = new Date(eventData.startDate);
        let endDate = new Date(eventData.endDate || eventData.startDate);
        
        const schoolName = userProfile.user.schoolName || "";
        const schoolRules = {
            "Itqan Australian Academy": 5,
            "Little Innovators School": 1,
            "MIT": 4,
        };
        if (schoolRules[schoolName] !== undefined) {
            const targetDay = schoolRules[schoolName];
            startDate.setDate(startDate.getDate() + ((targetDay - startDate.getDay() + 7) % 7));
            startDate.setHours(0, 0, 0);
            endDate = new Date(startDate);
            endDate.setHours(23, 59, 59);
        }

        // --- THIS IS THE FIX ---
        // We need to access the teamId from the nested user object: userProfile.user.teamId
        const userSpecificTimeline = timelines.find(t => t.team?.id === userProfile.user.teamId);

        return {
            id: group.eventData.id,
            title: eventData.title,
            start: startDate,
            end: endDate,
            allDay: true,
            extendedProps: {
                description: eventData.description || '',
                location: eventData.location || 'N/A',
                teams: teams,
                reservableTimelineId: userSpecificTimeline ? userSpecificTimeline.id : null,
            }
        };
    }).filter(Boolean);
};

// --- Main Hook ---
const useCalendarData = () => {
    const queryClient = useQueryClient();

    // 1. Fetch user profile
    const { data: userProfile, isLoading: isLoadingProfile } = useQuery({
        queryKey: ['userProfile'],
        queryFn: getUserProfile,
        staleTime: 1000 * 60 * 5,
    });
    
    const user = userProfile?.user;

    // 2. Fetch timelines for the user
    const { data: timelines, isLoading: isLoadingTimelines } = useQuery({
        queryKey: ['userTimelines', user?.id],
        queryFn: () => fetchUserTimelines(user.id),
        enabled: !!user?.id,
    });
    
    // 3. Fetch the user's existing reservations
    const { data: myReservations } = useQuery({
        queryKey: ['myReservations', user?.id],
        queryFn: fetchMyReservations,
        enabled: !!user?.id,
    });

    // 4. Memoize the list of reserved event IDs
    const myReservationEventIds = useMemo(() => {
        if (!myReservations) return [];
        return myReservations.map(res => res.eventId);
    }, [myReservations]);
    
    // 5. Memoize the formatted and grouped event data
    const events = useMemo(() => formatAndGroupEventData(timelines, userProfile), [timelines, userProfile]);

    // 6. Mutation for adding a new Event and its Timeline
    const addEventMutation = useMutation({
        mutationFn: async (eventData) => {
            if (!user) throw new Error("User not found.");
            const createdEvent = await createEvent({
                ...eventData,
                status: "planned",
            });
            return createTimeline({
                eventId: createdEvent.id,
                mentorId: user.id,
            });
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['userTimelines'] });
        },
    });

    // 7. Mutation for creating a Reservation
    const createReservationMutation = useMutation({
        mutationFn: apiCreateReservation,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['myReservations'] });
        },
    });

    // 8. Return all necessary data and functions
    return {
        isLoading: isLoadingProfile || isLoadingTimelines,
        events,
        user,
        myReservationEventIds,
        addEvent: addEventMutation.mutateAsync,
        createReservation: createReservationMutation.mutateAsync, 
    };
};

export default useCalendarData;